import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, fromEvent, map, merge, of, take } from 'rxjs';
import { networkActions } from 'src/app/store/features/network/network-actions';
import { NetworkStorageService } from './network-storage.service';

export enum NetworkConnectionType {
  Bluetooth = 'bluetooth',
  Cellular = 'cellular',
  Ethernet = 'ethernet',
  None = 'none',
  WiFi = 'wifi',
  WIMAX = 'wimax',
  Other = 'other',
  Unknown = 'unknown'
} 

@Injectable({
  providedIn: 'root',

})
export class NetworkService {
  public isOnline: boolean = false;
  public checkIsOnline$: Subscription = Subscription.EMPTY;
  private store$ = inject(Store);
  private http = inject(HttpClient);
  private networkStorage = inject(NetworkStorageService);

  public checkNetworkStatus(isPwa: boolean = false) : void{
    this.checkIsOnline$ = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')
    )
      .pipe(map(() => navigator.onLine))
      .subscribe(status => {
        if(status){
          this.retryConnection(isPwa)
        }else {
          this.isOnline = status;
          console.debug('❌ - Disconnected');
          this.store$.dispatch(networkActions.statusChanged({ isOnline: status }));
        }
      });
  }

  public retryConnection(isPwa: boolean): void {
    console.debug('Checking online status...');
    this.networkStorage.getAccessToken(isPwa).then(() => {
      this.http.get('api/ping', {
        headers: { 'ngsw-bypass': 'true' },
        responseType: 'text'
      }).pipe(take(1)).subscribe({
        next: () => {
          console.debug('✅ - Connected');
          this.isOnline = true;
          this.store$.dispatch(networkActions.statusChanged({ isOnline: true }));
        },
        error: (err: HttpErrorResponse) => {
          if (err.status > 0) {
            // Server responded (e.g. 401, 403, 500) — we are online
            console.debug('✅ - Connected (HTTP ' + err.status + ')');
            this.isOnline = true;
            this.store$.dispatch(networkActions.statusChanged({ isOnline: true }));
          } else {
            // status === 0 means no response at all (network failure, DNS, timeout, CORS)
            console.debug('❌ - Disconnected', err);
            this.isOnline = false;
            this.store$.dispatch(networkActions.statusChanged({ isOnline: false }));
          }
        }
      });
    });
  }

  public getStorage(isPwa: boolean): Storage {
    return this.networkStorage.getStorage(isPwa);
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation/type
  public detectConnectionType(): NetworkConnectionType {
    const connection = (navigator as any).connection;
    if (connection) {
      switch(connection.type) {
        case 'bluetooth':
          return NetworkConnectionType.Bluetooth;
        case 'cellular':
          return NetworkConnectionType.Cellular;
        case 'ethernet':
          return NetworkConnectionType.Ethernet;
        case 'none':
          return NetworkConnectionType.None;
        case 'wifi':
          return NetworkConnectionType.WiFi;
        case 'wimax':
          return NetworkConnectionType.WIMAX;
        case 'other':
          return NetworkConnectionType.Other;
        case 'unknown':
          return NetworkConnectionType.Unknown;
        default:
          return NetworkConnectionType.Unknown;
        }
      } else {
        console.log('Network Information API is not supported in this browser');
        return NetworkConnectionType.Unknown;
    }
  } 
}
